import { FunctionComponent, ReactElement } from "react";

import { MsalProvider, MsalProviderProps } from "@azure/msal-react";

import { PageTitleProvider } from "@/contexts/page-title";
import { UserProvider } from "@/contexts/user";
import { DataSourcesProvider } from "@/contexts/data-sources";
import { SignalRHubProvider } from "@/contexts/signalr-hub";

import "devextreme/dist/css/dx.common.css";
import "@/dx-styles.scss";
import "./index.css";
import { TaskServiceComponent } from "@/components/task-service";
import { TasksProvider } from "@/contexts/tasks";
import { ErrorProvider } from "@/contexts/error";
import { init, add } from "@amplitude/analytics-browser";
import { sessionReplayPlugin } from "@amplitude/plugin-session-replay-browser";
import * as sentry from "@sentry/react";
import config from "devextreme/core/config";
import { devextremeLicenseKey } from "@/config/devextreme-key";
import PageRoutes from "@/page-routes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { SidebarProvider } from "../ui/sidebar";
import { Root } from "./root";
import VersionWatcher from "../version-watcher";
import { PortalContainerProvider } from "@/contexts/portal";
import { SidebarStackerProvider } from "../ui/layouts";
import { DebugConsoleProvider } from "@/contexts/debug-console-context";

if (import.meta.env.VITE_AMPLITUDE_API_KEY !== undefined) {
    init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
        autocapture: { formInteractions: false },
    });
    const sessionReplayTracking = sessionReplayPlugin({
        sampleRate: 0.1,
    });
    add(sessionReplayTracking);
}

if (
    !!import.meta.env.VITE_SENTRY_DSN &&
    !!import.meta.env.VITE_ENVIRONMENT_NAME
) {
    sentry.init({
        environment: import.meta.env.VITE_ENVIRONMENT_NAME,
        dsn: import.meta.env.VITE_SENTRY_DSN,
        integrations: [
            sentry.browserTracingIntegration(),
            sentry.replayIntegration(),
        ],
        tracesSampleRate: 1.0, // Capture 100% of the transactions
        // Session Replay
        replaysSessionSampleRate: 0, // Set the sample rate at 0%.
        replaysOnErrorSampleRate: 1.0, // Change the sample rate to 100% when sampling sessions where errors occur.
    });
}

if (devextremeLicenseKey) {
    config({ licenseKey: devextremeLicenseKey });
}

const App: FunctionComponent<MsalProviderProps> = (
    props: MsalProviderProps,
): ReactElement => {
    const queryClient = new QueryClient({
        defaultOptions: {
            queries: {
                // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                // @ts-ignore
                retry(failureCount: number, error: { response: Response }) {
                    // Don't retry on 404s
                    if (
                        typeof error?.response === "object" &&
                        error?.response !== null &&
                        "status" in error.response &&
                        error?.response.status === 404
                    ) {
                        return false;
                    }
                    if (failureCount < 3) {
                        return true;
                    }
                    return false;
                },
            },
        },
    });

    return (
        <ErrorProvider>
            <title>Illuminate</title>
            <PageTitleProvider>
                <MsalProvider instance={props.instance}>
                    <UserProvider>
                        <QueryClientProvider client={queryClient}>
                            <DataSourcesProvider>
                                <SignalRHubProvider>
                                    <TasksProvider>
                                        <DebugConsoleProvider>
                                            <PortalContainerProvider>
                                                <SidebarProvider
                                                    defaultOpen={true}
                                                >
                                                    <SidebarStackerProvider>
                                                        <Root>
                                                            <PageRoutes />
                                                        </Root>
                                                    </SidebarStackerProvider>
                                                </SidebarProvider>
                                                <TaskServiceComponent />
                                                {!!import.meta.env
                                                    .VITE_ENVIRONMENT_NAME && (
                                                    <VersionWatcher />
                                                )}
                                            </PortalContainerProvider>
                                        </DebugConsoleProvider>
                                    </TasksProvider>
                                </SignalRHubProvider>
                            </DataSourcesProvider>
                        </QueryClientProvider>
                    </UserProvider>
                </MsalProvider>
            </PageTitleProvider>
        </ErrorProvider>
    );
};

export default App;
